/**
 * wsr-api tenant WStudio data reset client (WSRPDM-0049, WSRAPI-0081).
 *
 * Mirrors `./tenants.ts` conventions — wsr-api returns these DTOs RAW, no
 * `{success,data}` envelope, so this module never calls `unwrap()`. Errors
 * surface as `ApiError`; read the flat wsr-api body back via `wsrApiError()`
 * (`./errors.ts`).
 */
import { request } from '../client';
import type { WsrApiErrorBody } from './types';

const BASE = '/admin/platform';

/** §4 of the reset handoff — what a reset would delete, keyed by table name. */
export interface ResetPreview {
  tenantSlug: string;
  /** Per-table row counts a reset would delete, keyed by table name. */
  counts: Record<string, number>;
  resettable: boolean;
  /** e.g. ['TENANT_ENABLED', 'JOBS_IN_FLIGHT']; empty when resettable. */
  blockedBy: string[];
}

export interface ResetRequest {
  confirmTenantSlug: string;
  alsoDeleteWStudioConfig?: boolean;
}

/** The receipt — actual per-table deleted counts. */
export interface ResetReceipt {
  tenantSlug: string;
  counts: Record<string, number>;
}

/**
 * The 409 CONFLICT body — the guards are re-checked inside the delete
 * transaction (handoff §6), so this carries the same `blockedBy` reasons as
 * `ResetPreview` on top of the flat wsr-api error envelope.
 */
export interface ResetBlockedErrorBody extends WsrApiErrorBody {
  blockedBy?: string[];
}

/** GET /admin/platform/tenants/{tenantSlug}/reset/preview (WSRAPI-0081). */
export async function getResetPreview(slug: string): Promise<ResetPreview> {
  return request<ResetPreview>('GET', `${BASE}/tenants/${slug}/reset/preview`);
}

/** POST /admin/platform/tenants/{tenantSlug}/reset (WSRAPI-0081). */
export async function resetTenant(slug: string, body: ResetRequest): Promise<ResetReceipt> {
  return request<ResetReceipt>('POST', `${BASE}/tenants/${slug}/reset`, { json: body });
}
